import { toAlarmLocations, type FloorplanAlarmLocation } from "@nodus/floorplan";
import type { FloorAlarmRow, FloorplanAlarmRegionRow } from "@/lib/api/client";

/** Query key of `GET /tenant-portal/floor-alarms/` (the floors the member may see). */
export const FLOOR_ALARMS_KEY = ["floor-alarms"] as const;
export const FLOOR_ALARM_EVENT = "floor_alarm_changed";
/** Safety-net refetch while the member sees a floor with an alarm; the push covers the rest. */
export const FLOOR_ALARMS_POLL_MS = 5 * 60 * 1000;

/** Merge one `floor_alarm_changed` push into the cached list. A floor whose
 *  alarm was unlinked stays as a row with `state` null (so its `seq` still
 *  orders later pushes); an event older than the floor's last applied one
 *  (lower `seq`) is ignored. */
export function applyFloorAlarmEvent(rows: FloorAlarmRow[] | undefined, event: Record<string, unknown>): FloorAlarmRow[] {
  const list = rows ?? [];
  if (isRegionOnlyEvent(event)) return list; // a room / address: the floor plan's, not the banner's
  const floorId = Number(event.floor_id);
  if (!Number.isFinite(floorId)) return list;
  const seq = typeof event.seq === "number" ? event.seq : null;
  const current = list.find((r) => r.floor_id === floorId);
  if (seq != null && current?.seq != null && seq <= current.seq) return list;
  const row: FloorAlarmRow = {
    floor_id: floorId,
    floor_name: String(event.floor_name ?? current?.floor_name ?? ""),
    address_name: String(event.address_name ?? current?.address_name ?? ""),
    state: (event.state ?? null) as FloorAlarmRow["state"],
    since: (event.since as string | null | undefined) ?? null,
    warning_minutes: positiveMinutes(event.warning_minutes),
    seq,
  };
  return [...list.filter((r) => r.floor_id !== floorId), row];
}

/** Poll only while there is something to keep fresh. */
export function floorAlarmsPollInterval(rows: FloorAlarmRow[] | undefined): number | false {
  return rows?.some((r) => r.state != null) ? FLOOR_ALARMS_POLL_MS : false;
}

/** The member is in the building: the network check found the site's network
 *  (own or another company's). Remote, or still detecting, is not on site. */
export function isOnSite(networkStatus: string): boolean {
  return networkStatus === "own_network" || networkStatus === "wrong_network";
}

/** Floors to warn about now: pre-alarm, and only while the member is on site. */
export function preAlarmWarnings(rows: FloorAlarmRow[] | undefined, networkStatus: string): FloorAlarmRow[] {
  if (!isOnSite(networkStatus)) return [];
  return (rows ?? []).filter((r) => r.state === "pre_alarm");
}

export function floorLabel(row: Pick<FloorAlarmRow, "floor_name" | "address_name">): string {
  return row.address_name ? `${row.floor_name} (${row.address_name})` : row.floor_name;
}

/** The banner's dismiss key: one per pre-alarm episode (`since` is its start,
 *  kept by the backend through sensor drop-outs). */
export function warningKey(row: Pick<FloorAlarmRow, "floor_id" | "since">): string {
  return `${row.floor_id}:${row.since ?? ""}`;
}

function positiveMinutes(value: unknown): number | null {
  const n = Number(value);
  return value != null && Number.isFinite(n) && n > 0 ? n : null;
}

/** Minutes until the alarm arms, set per pre-alarm source; null when not set. */
export function warningMinutes(row: Pick<FloorAlarmRow, "warning_minutes">): number | null {
  return positiveMinutes(row.warning_minutes);
}

/** The banner's words: "in about N minutes" when the source says, else "soon". */
export function preAlarmWarningText(row: Pick<FloorAlarmRow, "floor_name" | "address_name" | "warning_minutes">): string {
  const minutes = warningMinutes(row);
  return minutes != null
    ? `The alarm on ${floorLabel(row)} turns on in about ${minutes} minutes. Use your tag at a door reader to keep it off.`
    : `The alarm on ${floorLabel(row)} turns on soon. Leave or use your tag at a door reader to keep it off.`;
}

/** When the warning started (the episode's `since`, its timestamp in both the
 *  read and the push) as "2026-10-08 21:45:00"; null when there is none. */
export function preAlarmWarningTime(row: Pick<FloorAlarmRow, "since">): string | null {
  if (!row.since) return null;
  const at = new Date(row.since);
  return Number.isNaN(at.getTime()) ? null : at.toLocaleString("sv-SE");
}

/** A push about an address or room with its own alarm links: one floor plan
 *  region, never a floor row (the on-site banner is per floor). Old pushes
 *  carry no `location_type` and are floors. */
export function isRegionOnlyEvent(event: Record<string, unknown>): boolean {
  return event.location_type != null && event.location_type !== "floor";
}

/** The plan's alarm regions, live: the payload's `alarm_regions`, with every
 *  floor of the plan (`floorIds`) taken from the pushed/polled floor list once
 *  it has loaded (a floor missing there, or with state null, has no alarm).
 *  Rooms and addresses come from the payload; their pushes refetch it. */
export function liveAlarmLocations(
  regions: FloorplanAlarmRegionRow[] | undefined,
  rows: FloorAlarmRow[] | undefined,
  floorIds: number[],
): FloorplanAlarmLocation[] {
  const payload = toAlarmLocations(regions);
  if (!rows) return payload;
  const onPlan = new Set(floorIds);
  const others = payload.filter((l) => !(l.locationType === "floor" && onPlan.has(l.locationId)));
  const floors = rows
    .filter((r) => onPlan.has(r.floor_id) && r.state)
    .map((r) => ({
      locationType: "floor" as const,
      locationId: r.floor_id,
      state: r.state!,
      since: r.since,
      warningMinutes: r.warning_minutes,
    }));
  return [...others, ...floors];
}
